import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XIcon, ArrowRightIcon } from 'lucide-react';
import { BRAND, DEMO_URL } from '../../data/dgpt';
import { CtaButton } from '../ui/CtaButton';
import { VideoPlayer } from './VideoPlayer';
type VideoLightboxProps = {
  onClose: () => void;
};

/**
 * Desktop lightbox that plays the demo at min(90vw, 1200px) so the product UI
 * is legible. The close control sits above the player — never on top of it.
 */
export function VideoLightbox({
  onClose
}: VideoLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose]);
  return createPortal(<div role="dialog" aria-modal="true" aria-label="DiligenceGPT™: Venture Capital video" className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0A0A0A]/85 p-4" onClick={onClose} data-id="element-402" data-mp="16wvhm2ad3qka">
      <div className="flex flex-col gap-3" style={{
      width: 'min(90vw, 1200px)'
    }} onClick={event => event.stopPropagation()} data-id="element-403" data-mp="1kutjw9berwjf">
        <div className="flex items-center justify-between gap-4" data-id="element-404" data-mp="1psv8tavimq2t">
          <p className="text-sm font-semibold text-white" data-id="element-405" data-mp="1bsu1bckqwked">
            {BRAND.product} <span className="font-normal text-white/70" data-id="element-406" data-mp="1fuxmwu14p5dd">· {BRAND.tagline}</span>
          </p>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close video" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-407" data-mp="eymy61o0fo7">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-408" data-mp="1xd5x5jo5rvev" />
          </button>
        </div>

        <VideoPlayer data-id="element-409" data-mp="1xat52doos4o3" />

        <div className="flex justify-end" data-id="element-410" data-mp="1secl7voiscgt">
          <CtaButton href={DEMO_URL} event="hero_book_demo_click" className="uppercase tracking-[0.06em]" data-id="element-411" data-mp="1sdjjj94f4d5j">
            Book a Demo
            <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-412" data-mp="1oa4szoiusedu" />
          </CtaButton>
        </div>
      </div>
    </div>, document.body);
}